import { omniaResolve } from "../promises"
import { IOmniaShell } from "../shell"
import { UpdatableComponent } from "../shell/component"
import { checkModalScope } from "../shell/dialog"
import { defineComponent } from "../utils/angular"

/**
 * Extends React.FunctionComponent with an "inject" property,
 * used to define services to inject from Angular
 */
export interface ReactInjectableFunctionComponent<P = {}> extends React.FunctionComponent<P> {
    readonly inject?: Record<string, string>
}

/**
 * This function defines an Angular component implemented using React
 */
export function defineReactComponent(cmp: ReactAngularComponent): ng.IDirective {

    const def = defineComponent(Controller, null, cmp.scope)

    def.template = "<div></div>"
    def.link = (scope, element, attrs, ctrl: Controller) => ctrl.link(element, cmp)

    return def


}

/**
 * This function defines the omnia-react-component directive
 * It has a similar role to defineReactComponent() above, but the React function/component
 * is expected to be passed through a standard Omnia component definition
 * This is ultimately used to start React components throught the startComponent() function
 */
export function defineGenericReactComponent(): ng.IDirective {

    const def = defineComponent(Controller, null, {
        componentContext: "<"
    })

    def.template = "<div></div>"

    def.link = (scope, element, attrs, ctrl: Controller) => {

        let currentContext: ReactAngularComponentContext

        ctrl.link(element, {

            start(context: ReactAngularComponentContext) {
                currentContext = context
                start(context)
            },

            refresh(auto: boolean) {

                if (currentContext) {
                    start({
                        ...currentContext,
                        refresh: { auto }
                    })
                }

                return undefined

            }

        })

        function start(context: ReactAngularComponentContext) {

            const reactComponent = context.reactComponent

            const { rootElement } = context
            const element = React.createElement(reactComponent, context as unknown)

            ReactDOM.render(element, rootElement)

        }

    }


    return def
}

/**
 * Definition of a React/Angular component
 */
export interface ReactAngularComponent {

    /**
     * This function is called from Angular, and should implement the  React component
     * The argument can be used to get access to various properties/services,
     * such as the root DOM element where the React component should be hosted
     */
    start(context: ReactAngularComponentContext): void

    /**
     * Called when a refresh is requested
     * @param auto Used to differentiate and automatic or manual refresh
     */
    refresh?(auto: boolean): undefined | PromiseLike<any>

    /**
     * Global Angular services required by the component must be defined here
     */
    inject?: { [key: string]: string }

    /**
     * Standard Angular scope definition, to support bindings
     */
    scope?: ng.IDirective["scope"]

    /**
     * Initial title/icon to display in the main header
     */
    header?: {
        title: string,
        icon: string
    }

}

/**
 * Defines a context, passed from AngularJS to React, to provide various services
 */
export interface ReactAngularComponentContext<Dependencies = {}, Props = any> {

    /** Root DOM element, usable with React DOM */
    rootElement: HTMLElement

    /** Dependencies requested through the "inject" property  */
    dependencies: Dependencies

    /**
     * The view model (i.e., the instance of the controller in our context)
     * is provided in order to give access to bindings
     */
    vm: any

    /** Call this to the component close itself */
    close(result?: any): void

    /**
     * Arguments passed by the caller
     */
    arguments?: Props

    /*
    * Function component passed by the caller
    * Only set when using omnia-react-component
    */
    reactComponent?: ReactInjectableFunctionComponent

    /**
     * Set if refreshing an existing instance
     */
    refresh?: { auto: boolean }

    /**
     * Can be called to set/update the title and icon in the main header
     */
    setHeader(header: { title: string, icon: string }): void

}

class Controller extends UpdatableComponent {

    /** Root HTML, set when link() is called */
    private rootElement: HTMLElement

    /** Component definition, set when link() is called */
    private cmp: ReactAngularComponent

    /** Indicates if $onInit() has been called */
    private init = false

    /** Indicates is the React component has already been started */
    private started = false


    static $inject = ["$scope", "$rootScope", "$injector", "OmniaShell"]

    constructor(
        $scope: ng.IScope,
        private readonly $rootScope: ng.IRootScopeService,
        private readonly $injector: ng.auto.IInjectorService,
        private readonly shell: IOmniaShell
    ) {
        super($scope)
    }

    /** This is called automatically by Angular when the component is ready */
    $onInit() {
        this.init = true
        this.start()
    }

    /** This is called automatically by Angular when the component is destroyed/removed from the DOM */
    $onDestroy() {

        const { rootElement } = this

        if (rootElement) {
            ReactDOM.unmountComponentAtNode(rootElement)
        }

    }

    /** This is called automatically when the Angular component is being instantiated */
    link(element: ng.IAugmentedJQuery, cmp: ReactAngularComponent) {
        this.cmp = cmp
        this.rootElement = element[0]
        this.start()
    }

    refresh(auto: boolean): PromiseLike<any> {

        const { cmp } = this

        return cmp?.refresh(auto) ?? omniaResolve()

    }

    /**
     * This is called to start the React component, once everything is ready
     */
    private start() {

        const { rootElement, cmp, init, started, $scope, $injector, componentContext } = this

        if (started) return
        if (!(rootElement && init)) return

        // This checks if the component is instantiated as a "modal" (i.e., dialog)
        // If it is, then the resulting "dialog" object will be set, and include a "close()" function
        // which can be called to close said dialog
        const dialog = checkModalScope($scope)

        // Quick/temp workaroud to prevent closing dialog when pressing "esc"
        // When a dialog (modal in Angular UI parlance) is closed, a "modal.closing"
        // event will be dispatched to its scope, and calling "preventDefault()" on this event
        // will prevent it from being closed
        const modalCloseWatcher = dialog
            ? $scope.$on("modal.closing", event => {
                event.preventDefault()
            })
            : undefined

        const inject = cmp.inject ?? componentContext?.reactComponent?.inject

        const dependencies = getAngularDependencies($injector, inject)

        const setHeader = (header: { title: string, icon: string }) => {
            this._title = header?.title
            this._icon = header?.icon
            $scope.$applyAsync()
        }

        setHeader(cmp.header)

        this.started = true

        cmp.start({

            rootElement,

            vm: this,

            dependencies,

            close: (result?: any) => {

                this.$rootScope.$applyAsync()

                if (dialog) {
                    // This is called by React when the dialog should be closed,
                    // so we start by removing the "modal.close" event listener defined earlier
                    // so that it does not prevent this from happening
                    modalCloseWatcher()
                    // And then we can close the dialog
                    dialog.close(result)
                }
                else {
                    this.closeComponent(result)
                }

            },

            arguments: dialog
                ? dialog.arguments
                : componentContext?.execArguments,

            reactComponent: componentContext?.reactComponent,

            setHeader

        })


    }

}

export function getAngularDependencies($injector: ng.auto.IInjectorService, inject: { [key: string]: string }) {

    const dependencies = {}

    if (inject) {

        for (const key in inject) {
            const name = inject[key]
            dependencies[key] = $injector.get(name)
        }

    }

    return dependencies

}
